import { CheckoutDescription } from "@circle-react/components/Paywalls/Checkout/CheckoutPage/CheckoutDescription";
import { TrackingCodeInjector } from "@circle-react/components/shared/Paywalls/TrackingCodeInjector";
import { useEffect } from "react";

import { t } from "@/i18n-js/instance";
import { Button } from "@/react/components/SettingsApp/CompassCompat/Button";

import { useCheckoutSession } from "../../../Checkout/contexts/CheckoutSessionContext";
import { firePurchaseForCompletedSteps } from "../../helpers/ga4PurchaseHelper";
import { getMainStep } from "../../helpers/SessionStepQueryHelper";
import { useGoToNextStep } from "../../hooks/useGoToNextStep";
import { FadeEffect } from "../OfferPage/components/FadeEffect";
import { Container } from "./components/Container";
import { Title } from "./components/Title";
import { PaywallList } from "./PaywallList";
import { SummaryPrice } from "./SummaryPrice";

export const ConfirmationPage = () => {
  const { session, ga4FiredStepIdsRef } = useCheckoutSession();
  const community = session?.community;

  const mainStep = getMainStep(session?.steps || []);

  const { redirectAfterConfirmation } = useGoToNextStep();

  // Fire GA4 purchase events for each completed step on mount and whenever
  // session.steps changes. The shared ga4FiredStepIdsRef deduplicates against
  // any events already fired by ConfirmationRoute, useGoToNextStep, or
  // OfferPage/AttemptsProvider.onComplete. Upsell steps fired at onComplete
  // time are already marked in ga4FiredStepIdsRef, so this effect is a no-op
  // for them; it handles the main step and any edge-case steps not already fired.
  useEffect(() => {
    firePurchaseForCompletedSteps(session, ga4FiredStepIdsRef);
    // session?.steps as the dep: re-run when a new step completes.
    // ga4FiredStepIdsRef is a stable ref — its identity never changes so it
    // does not need to be in the dep array.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ga4FiredStepIdsRef is a stable ref; session?.steps is intentional subset of session
  }, [session?.steps]);

  if (!community) {
    return null;
  }

  const communityMemberName = session?.community_member?.first_name;

  return (
    <>
      <TrackingCodeInjector
        trackingJsCode={mainStep?.conversion_tracking_js_code}
      />
      <div
        className="flex flex-col size-full items-center bg-secondary align-middle"
        data-testid="checkout-confirmation-page"
      >
        <FadeEffect className="flex flex-col gap-y-12 justify-center size-full align-bottom md:align-middle md:w-[500px] py-8 md:py-0">
          <CheckoutDescription.CommunityHeader
            community={community}
            className="!justify-center !py-0 h-full md:h-auto items-center md:items-start"
          />
          <Container>
            <Title memberName={communityMemberName} />
            <PaywallList />
            <SummaryPrice />
            <Button
              full
              large
              variant="circle"
              type="button"
              onClick={() => redirectAfterConfirmation(session)}
            >
              {t("continue")}
            </Button>
          </Container>
        </FadeEffect>
      </div>
    </>
  );
};
